// @givmo/board — how a record's lines name the person a roster seat was ON THE DAY OF THE ACT
// (CBG-FIX49; backend `board-officer-roles.md` §16): the titles the seat held that day, else "a
// director", else the name alone. In an organization's own usage its "board members" are its
// directors, so the old "a board member" was untrue of a Secretary who isn't one.
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// The approval and disclosure forms are APPROVED (Jonathan, 2026-10-13 12:09 PDT, "good"): "As approved
// by Ruth Calloway, Chair, on …", "As approved by Theo Barrett, a director, on …", "As approved by
// Iris Okafor on …", "Disclosed by Maren Lindqvist, Secretary and Treasurer; recorded on …". The rule
// was widened to the record's history lines at 12:39 PDT ("agree"), and THOSE lines' exact words, with
// the fallback for a seat that doesn't resolve (the orchestrator's C13), are approved too: copy round
// CBG-RC11-COPY1, 2026-10-13 21:51 PDT (`scratchpad/2026-10-13-rc11-copy-round/copy-review.html`, §7,
// beside the forms they replace).

import { joinWords } from '../roster/officer-roles';

/**
 * A seat named as the person was that day: "Ruth Calloway, Chair", "Theo Barrett, a director",
 * "Iris Okafor". Null when the name didn't resolve: the caller says so in its own words. A blank
 * title is dropped, so a line never ends in a dangling comma.
 */
export function seatPerson(
  name: string | null,
  titleLabels: readonly (string | null | undefined)[] | null | undefined,
  isDirector: boolean | null | undefined,
): string | null {
  if (name === null) return null;
  const titles = (titleLabels ?? [])
    .map((label) => (typeof label === 'string' ? label.trim() : ''))
    .filter((label) => label.length > 0);
  if (titles.length > 0) return `${name}, ${joinWords(titles)}`;
  // APPROVED (CBG-FIX49).
  if (isDirector === true) return `${name}, a director`;
  return name;
}

export const recordPersonWords = {
  // A seat that doesn't resolve, wherever "a board member whose name isn’t shown" stood (C13):
  // nothing is known of it, so it reads as an account that doesn't resolve does.
  unnamed: 'someone whose name isn’t shown',
  // The same, opening a line.
  unnamedOpening: 'Someone whose name isn’t shown',
  // A seat's own act in the record's history ("By Maren Lindqvist, Secretary and Treasurer"; was
  // "By Maren Lindqvist, as a board member").
  by: (person: string) => `By ${person}`,
  // An officer's entry for a seat ("Recorded by Maya Solberg for Theo Barrett, a director"; was
  // "…for Theo Barrett, a board member").
  recordedFor: (officer: string, person: string) => `Recorded by ${officer} for ${person}`,
  // A filed consent's making event, the director's own last signature.
  lastSignature: (person: string) => `The last signature: ${person}`,
  // The same, entered by an officer.
  lastSignatureRecordedFor: (officer: string, person: string) =>
    `The last signature, recorded by ${officer} for ${person}`,
} as const;
